import React, { createContext, useCallback, useContext, useEffect, useRef, ReactNode } from "react";
import {
  useSaveSearchAnalyticsMutation,
  useSaveNoResultSearchAnalyticsMutation,
} from "Services/SearchAnalyticsApiSlice";
import { useGetCategoriesByProductTypeQuery } from "Services/CategoryApiSlice";
import { decodeParamList, decodeParam } from "Utilities/urlEncoder";

import { UTCTimestamp, toUTCTimestamp } from "Utilities/DateTimeUtils";

interface AnalyticsEntry {
  searchType: string;
  searchData: string;
  searchDateTime: UTCTimestamp;
  selectedProductId?: string | null;
}

export interface FilterItem {
  type: string;
  value: string;
}

export const getActiveFiltersFromUrl = (categoriesData?: any[]): FilterItem[] => {
  if (typeof window === "undefined") return [];
  const searchParams = new URLSearchParams(window.location.search);
  const filters: FilterItem[] = [];

  // 1. Search Bar Query
  const search = searchParams.get("search");
  if (search) {
    filters.push({ type: "SEARCH_BAR", value: decodeURIComponent(search) });
  }

  // 2. Category
  const category = searchParams.get("category");
  if (category) {
    decodeParamList(category).forEach(val => {
      let resolvedValue = val;
      if (categoriesData) {
        const cat = categoriesData.find((c: any) => c.categoryId === val || c.categoryName === val);
        if (cat) {
          resolvedValue = cat.categoryName;
        }
      }
      filters.push({ type: "CATEGORY", value: resolvedValue });
    });
  }

  // 3. Sub Category
  const subCategory = searchParams.get("subCategory");
  if (subCategory) {
    decodeParamList(subCategory).forEach(val => {
      const displayVal = val.includes("_") ? val.split("_")[1] : val;
      if (displayVal !== "none" && displayVal !== "None") {
        let resolvedSubValue = displayVal;
        if (categoriesData) {
          for (const cat of categoriesData) {
            if (cat.subClasses) {
              const matched = cat.subClasses.find(
                (sub: any) => sub.subClassId === displayVal || sub.subClassName === displayVal
              );
              if (matched) {
                resolvedSubValue = matched.subClassName;
                break;
              }
            }
          }
        }
        filters.push({ type: "SUB_CATEGORY", value: resolvedSubValue });
      }
    });
  }

  // 4. Author
  const author = searchParams.get("author");
  if (author) {
    decodeParamList(author).forEach(val => {
      filters.push({ type: "AUTHOR", value: val });
    });
  }

  // 5. Language
  const language = searchParams.get("language");
  if (language) {
    decodeParamList(language).forEach(val => {
      filters.push({ type: "LANGUAGE", value: val });
    });
  }

  // 6. Academic
  const academic = searchParams.get("academic");
  if (academic) {
    decodeParamList(academic).forEach(val => {
      filters.push({ type: "ACADEMIC", value: val });
    });
  }

  // 7. Book Condition
  const bookCondition = searchParams.get("bookCondition");
  if (bookCondition) {
    decodeParamList(bookCondition).forEach(val => {
      filters.push({ type: "BOOK_CONDITION", value: val === "PreOwned" ? "Pre-Owned" : val });
    });
  }

  // 8. Merchandise
  const merchandise = searchParams.get("merchandise");
  if (merchandise) {
    decodeParamList(merchandise).forEach(val => {
      filters.push({ type: "MERCHANDISE", value: val });
    });
  }

  // 9. Price
  const fromprice = searchParams.get("fromprice");
  const toprice = searchParams.get("toprice");
  if (fromprice) {
    const from = decodeParam(fromprice);
    const to = toprice ? decodeParam(toprice) : null;
    const priceRangeText = to ? `Rs. ${from} - Rs. ${to}` : `Rs. ${from} above`;
    filters.push({ type: "PRICE", value: priceRangeText });
  }

  // 10. Product Type
  const productType = searchParams.get("productType");
  if (productType) {
    try {
      decodeParamList(productType).forEach(val => {
        const upper = val.toUpperCase();
        filters.push({ type: "PRODUCT_TYPE", value: upper });
      });
    } catch (e) {
      // ignore
    }
  }

  return filters;
};

interface SearchAnalyticsContextType {
  queueAnalytics: (searchType: string, searchData: string, selectedProductId?: string | null) => void;
  resolveAnalytics: (hasResults: boolean, resultCount?: number) => void;
  attachProductToLastSearch: (productId: string, productName?: string, source?: "card" | "detail", productType?: string) => void;
  clearAnalyticsQueue: () => void;
}

const SEARCH_LOGS_KEY = "sa_search_logs";
const NO_RESULT_LOGS_KEY = "sa_no_result_logs";
// const FLUSH_INTERVAL_MS = 3_600_000; // 1 hour
const FLUSH_INTERVAL_MS = 60_000; // 1 min

const SearchAnalyticsContext = createContext<SearchAnalyticsContextType | undefined>(undefined);

const getDateTime = (): UTCTimestamp => toUTCTimestamp(new Date().toISOString());

const readLogs = (key: string): AnalyticsEntry[] => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};

const writeLogs = (key: string, logs: AnalyticsEntry[]): void => {
  localStorage.setItem(key, JSON.stringify(logs));
};

export const getAnalyticsContextFromUrl = (defaultData: string, productId: string, categoriesData?: any[]) => {
  if (typeof window === "undefined") return { searchType: "BOOK", searchData: defaultData, selectedProductId: productId };
  
  const searchParams = new URLSearchParams(window.location.search);
  
  const urlToTypeMap: Record<string, string> = {
    search: "SEARCH_BAR",
    subCategory: "SUB_CATEGORY",
    category: "CATEGORY",
    author: "AUTHOR",
    merchandise: "MERCHANDISE",
    language: "LANGUAGE",
    academic: "ACADEMIC",
    bookCondition: "BOOK_CONDITION",
    fromprice: "PRICE",
  };

  const activeTypes = new Set<string>();
  for (const [key, type] of Object.entries(urlToTypeMap)) {
    if (searchParams.has(key)) {
      activeTypes.add(type);
    }
  }

  if (activeTypes.size > 0) {
    try {
      const logs = JSON.parse(localStorage.getItem("sa_search_logs") || "[]");
      for (let i = logs.length - 1; i >= 0; i--) {
        const log = logs[i];
        if (activeTypes.has(log.searchType) && log.searchData) {
          return {
            searchType: log.searchType,
            searchData: log.searchData,
            selectedProductId: productId
          };
        }
      }
    } catch {
      // ignore
    }

    const orderedKeys = [
      "search",
      "subCategory",
      "category",
      "author",
      "merchandise",
      "language",
      "academic",
      "bookCondition",
      "fromprice",
    ];

    for (const key of orderedKeys) {
      if (searchParams.has(key)) {
        let fallbackData = defaultData;
        if (key === "search") fallbackData = decodeURIComponent(searchParams.get(key) || defaultData);
        else if (key === "fromprice") {
           const from = decodeParam(searchParams.get("fromprice") || "");
           const to = searchParams.has("toprice") ? decodeParam(searchParams.get("toprice") || "") : "above";
           fallbackData = `${from}-${to}`;
        } else {
           const decoded = decodeParamList(searchParams.get(key) || "")[0] || defaultData;
           let resolved = decoded;
           if (key === "category" && categoriesData) {
             const cat = categoriesData.find((c: any) => c.categoryId === decoded || c.categoryName === decoded);
             if (cat) {
               resolved = cat.categoryName;
             }
           } else if (key === "subCategory") {
             const subId = decoded.includes("_") ? decoded.split("_")[1] : decoded;
             if (subId !== "none" && subId !== "None") {
               resolved = subId;
               if (categoriesData) {
                 for (const cat of categoriesData) {
                   if (cat.subClasses) {
                     const matched = cat.subClasses.find(
                       (sub: any) => sub.subClassId === subId || sub.subClassName === subId
                     );
                     if (matched) {
                       resolved = matched.subClassName;
                       break;
                     }
                   }
                 }
               }
             }
           }
           fallbackData = resolved;
        }
        return {
          searchType: urlToTypeMap[key],
          searchData: fallbackData,
          selectedProductId: productId
        };
      }
    }
  }
  
  return { searchType: "BOOK", searchData: defaultData, selectedProductId: productId };
};

export const SearchAnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [saveSearchAnalytics] = useSaveSearchAnalyticsMutation();
  const [saveNoResultSearchAnalytics] = useSaveNoResultSearchAnalyticsMutation();
  const { data: categoriesData } = useGetCategoriesByProductTypeQuery("BOOK");

  const pendingRef = useRef<{ searchType: string; searchData: string; selectedProductId?: string | null }[]>([]);
  const flushRef = useRef<() => Promise<void>>();

  const flushAnalytics = useCallback(async () => {
    const searchLogs = readLogs(SEARCH_LOGS_KEY);
    const noResultLogs = readLogs(NO_RESULT_LOGS_KEY);

    if (searchLogs.length > 0) {
      try {
        await saveSearchAnalytics({
          origin: window.location.origin,
          body: { analyticsList: searchLogs },
        }).unwrap();
        writeLogs(SEARCH_LOGS_KEY, []);
      } catch {
        // keep data; will retry on next flush
      }
    }

    if (noResultLogs.length > 0) {
      try {
        await saveNoResultSearchAnalytics({
          origin: window.location.origin,
          body: { analyticsList: noResultLogs },
        }).unwrap();
        writeLogs(NO_RESULT_LOGS_KEY, []);
      } catch {
        // keep data; will retry on next flush
      }
    }
  }, [saveSearchAnalytics, saveNoResultSearchAnalytics]);

  useEffect(() => {
    flushRef.current = flushAnalytics;
  }, [flushAnalytics]);

  useEffect(() => {
    const interval = setInterval(() => {
      flushRef.current?.();
    }, FLUSH_INTERVAL_MS);

    // Flush immediately when connection is restored instead of waiting for the next tick
    const handleOnline = () => flushRef.current?.();
    window.addEventListener("online", handleOnline);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const queueAnalytics = useCallback((searchType: string, searchData: string, selectedProductId?: string | null) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return;
    }
    const trimmed = searchData.trim();
    if (!trimmed) return;
    pendingRef.current.push({ searchType, searchData: trimmed, selectedProductId });
  }, []);

  const resolveAnalytics = useCallback((hasResults: boolean, resultCount?: number) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      pendingRef.current = [];
      return;
    }

    const isDirectAction = resultCount === undefined;
    const hasGlobalAction = pendingRef.current.some(
      item => item.searchType === "SEARCH_BAR" || item.searchType === "BOOK"
    );

    if (pendingRef.current.length > 0 && (isDirectAction || hasGlobalAction)) {
      const pending = pendingRef.current.splice(0);
      const newLogs: AnalyticsEntry[] = pending.map((item) => ({
        searchType: item.searchType,
        searchData: item.searchData,
        searchDateTime: getDateTime(),
        ...(item.selectedProductId ? { selectedProductId: item.selectedProductId } : {}),
      }));

      if (hasResults) {
        writeLogs(SEARCH_LOGS_KEY, [...readLogs(SEARCH_LOGS_KEY), ...newLogs]);
      } else {
        writeLogs(NO_RESULT_LOGS_KEY, [...readLogs(NO_RESULT_LOGS_KEY), ...newLogs]);
      }
      return;
    }

    const activeFilters = getActiveFiltersFromUrl(categoriesData);
    if (activeFilters.length > 0) {
      const productTypeFilters = activeFilters.filter(f => f.type === "PRODUCT_TYPE");
      const otherFilters = activeFilters.filter(f => f.type !== "PRODUCT_TYPE");
      
      let processedFilters = [...activeFilters];
      const hasMultipleProductTypes = productTypeFilters.length > 1;
      
      if (hasMultipleProductTypes) {
        const combinedValues = productTypeFilters.map(f => {
          const upper = f.value.toUpperCase();
          if (upper === "BOOK") return "Book";
          if (upper === "RECORD") return "Record";
          if (upper === "MERCHANDISE") return "Merchandise";
          return f.value;
        }).join(", ");
        
        processedFilters = [
          ...otherFilters,
          { type: "PRODUCT_TYPE", value: combinedValues }
        ];
      }

      const distinctTypes = Array.from(new Set(processedFilters.map(f => f.type)));
      const distinctFilterTypesCount = distinctTypes.length;

      let newLog: AnalyticsEntry;

      if (distinctFilterTypesCount === 1 && !hasMultipleProductTypes) {
        const singleType = distinctTypes[0];
        const values = processedFilters.filter(f => f.type === singleType).map(f => f.value);
        const joinedValues = values.join(", ");

        newLog = {
          searchType: singleType,
          searchData: joinedValues,
          searchDateTime: getDateTime(),
          selectedProductId: null
        };
      } else {
        // Group all active filters into a single MULTIPLE_FILTERS search log
        const groupedData = JSON.stringify({
          filters: processedFilters,
          resultCount: resultCount !== undefined ? resultCount : (hasResults ? 1 : 0)
        });

        newLog = {
          searchType: "MULTIPLE_FILTERS",
          searchData: groupedData,
          searchDateTime: getDateTime(),
          selectedProductId: null
        };
      }

      if (hasResults) {
        writeLogs(SEARCH_LOGS_KEY, [...readLogs(SEARCH_LOGS_KEY), newLog]);
      } else {
        writeLogs(NO_RESULT_LOGS_KEY, [...readLogs(NO_RESULT_LOGS_KEY), newLog]);
      }

      // Clear the pending queue as these checkbox clicks/filters are already recorded grouped
      pendingRef.current = [];
      return;
    }

    const pending = pendingRef.current.splice(0);
    if (pending.length === 0) return;

    const newLogs: AnalyticsEntry[] = pending.map((item) => ({
      searchType: item.searchType,
      searchData: item.searchData,
      searchDateTime: getDateTime(),
      ...(item.selectedProductId ? { selectedProductId: item.selectedProductId } : {}),
    }));

    if (hasResults) {
      writeLogs(SEARCH_LOGS_KEY, [...readLogs(SEARCH_LOGS_KEY), ...newLogs]);
    } else {
      writeLogs(NO_RESULT_LOGS_KEY, [...readLogs(NO_RESULT_LOGS_KEY), ...newLogs]);
    }
  }, [categoriesData]);

  const clearAnalyticsQueue = useCallback(() => {
    pendingRef.current = [];
  }, []);

  const attachProductToLastSearch = useCallback((productId: string, productName?: string, source?: "card" | "detail", productType?: string) => {
    const searchLogs = readLogs(SEARCH_LOGS_KEY);
    
    // Check if the last log is a MULTIPLE_FILTERS log
    if (searchLogs.length > 0) {
      const lastIndex = searchLogs.length - 1;
      if (searchLogs[lastIndex].searchType === "MULTIPLE_FILTERS") {
        if (!searchLogs[lastIndex].selectedProductId) {
          searchLogs[lastIndex].selectedProductId = productId;
          writeLogs(SEARCH_LOGS_KEY, searchLogs);
          return;
        } else if (searchLogs[lastIndex].selectedProductId === productId) {
          return;
        }
      }
    }

    const context = getAnalyticsContextFromUrl(productName || "BOOK", productId, categoriesData);

    const mapProductTypeToSearchType = (pt?: string): string => {
      if (!pt) return "BOOK";
      const upper = pt.toUpperCase();
      if (upper === "RECORD") return "RECORD";
      if (upper === "MERCHANDISE") return "MERCHANDISE";
      return "BOOK";
    };

    const targetSearchType = context.searchType === "BOOK" ? mapProductTypeToSearchType(productType) : context.searchType;
    const targetSearchData = context.searchType === "BOOK" ? (productName || targetSearchType) : context.searchData;

    // Duplication check: if the last log is already the same searchType/data/product, skip
    if (searchLogs.length > 0) {
      const lastIndex = searchLogs.length - 1;
      if (
        searchLogs[lastIndex].searchType === targetSearchType &&
        searchLogs[lastIndex].searchData === targetSearchData &&
        searchLogs[lastIndex].selectedProductId === productId
      ) {
        return;
      }
    }

    let foundIndex = -1;
    for (let i = searchLogs.length - 1; i >= 0; i--) {
      if (searchLogs[i].searchType === targetSearchType && searchLogs[i].searchData === targetSearchData) {
        foundIndex = i;
        break;
      }
    }
    
    if (foundIndex !== -1) {
      if (!searchLogs[foundIndex].selectedProductId) {
        searchLogs[foundIndex].selectedProductId = productId;
        writeLogs(SEARCH_LOGS_KEY, searchLogs);
        return;
      } else if (searchLogs[foundIndex].selectedProductId === productId) {
        return;
      }
    }
    
    // If source is detail and we reached here (no matching log found),
    // do not create a fallback log.
    if (source === "detail") {
      return;
    }
    
    queueAnalytics(targetSearchType, targetSearchData, productId);
    resolveAnalytics(true);
  }, [queueAnalytics, resolveAnalytics, categoriesData]);

  return (
    <SearchAnalyticsContext.Provider value={{ queueAnalytics, resolveAnalytics, attachProductToLastSearch, clearAnalyticsQueue }}>
      {children}
    </SearchAnalyticsContext.Provider>
  );
};

export const useSearchAnalytics = (): SearchAnalyticsContextType => {
  const context = useContext(SearchAnalyticsContext);
  if (!context) throw new Error("useSearchAnalytics must be used within SearchAnalyticsProvider");
  return context;
};
